/**
 * Member Activity section — the consolidated history surface (DNKADM-0246). Two inner
 * tabs over the member-scoped credit histories that used to be scattered across the
 * detail screen: Credit history (the holistic credit ledger, formerly at the bottom of
 * Membership/Credits) and the Activity ledger (credit-ledger entries). The payment
 * ledger (Transactions) lived here as a third inner tab until DNKADM-0391 gave it the
 * top-level Payments tab; it is not duplicated here. Each child owns its own data
 * loading, paging and filters; this wrapper owns only which one is shown.
 *
 * Only the selected inner panel is mounted, mirroring how `MemberDetail` gates its own
 * panels — an unselected surface issues no requests. The selection lives in the URL
 * (`?activity=credit-history|ledger`, DNKADM-0430 P1-D), so a link or a reload reopens it
 * and it survives leaving Activity and coming back. The panels still unmount on an outer
 * tab change, which is why Credit history needs no `reloadToken` key here: it remounts and
 * refetches on entry, and no adjust affordance lives on this tab.
 *
 * The inner a11y ids are deliberately DISTINCT from the outer `member-detail-*` ones so
 * the two tab lists never collide on `aria-controls`/`aria-labelledby`.
 */
import { Box, Tab, Tabs } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { parseOption, useListUrlState } from '@/components/list/useListUrlState';
import { resolveTokens } from '@/theme';
import { MemberActivityTab } from './MemberActivityTab';
import { MemberCreditHistory } from './MemberCreditHistory';
import {
  MEMBER_ACTIVITY_TABS,
  MEMBER_ACTIVITY_URL_DEFAULTS,
  type MemberActivityInnerTab,
} from './memberDetailView';

/** The inner `Tab` values, in display order (index = the a11y id suffix). */
const INNER_TAB_INDEX: Record<MemberActivityInnerTab, number> = { 'credit-history': 0, ledger: 1 };

function memberActivityTabA11yProps(index: number) {
  return {
    id: `member-activity-tab-${index}`,
    'aria-controls': `member-activity-tabpanel-${index}`,
  };
}

function memberActivityPanelA11yProps(index: number) {
  return {
    role: 'tabpanel' as const,
    id: `member-activity-tabpanel-${index}`,
    'aria-labelledby': `member-activity-tab-${index}`,
    tabIndex: 0,
  };
}

export function MemberActivitySection({ contactId }: { contactId: string }) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const { values, set } = useListUrlState(MEMBER_ACTIVITY_URL_DEFAULTS);
  const inner =
    INNER_TAB_INDEX[parseOption(values.activity, MEMBER_ACTIVITY_TABS, 'credit-history')];
  const setInner = (index: number) => set({ activity: MEMBER_ACTIVITY_TABS[index] ?? null });

  return (
    <>
      <Tabs
        value={inner}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        onChange={(_, v: number) => setInner(v)}
        aria-label="Member activity sections"
        sx={{
          // Subordinate to the primary detail tabs: a section control inside a panel —
          // shorter, lighter rule, same brand selected/indicator treatment.
          minHeight: 36,
          borderBottom: `1px solid ${t.lineSoft}`,
          '& .MuiTab-root': { minHeight: 36, py: 0.5, fontSize: 12 },
          '& .MuiTabs-indicator': { bgcolor: t.brand },
          '& .Mui-selected': { color: `${t.brand} !important` },
        }}
      >
        <Tab label="Credit history" {...memberActivityTabA11yProps(0)} />
        <Tab label="Activity ledger" {...memberActivityTabA11yProps(1)} />
      </Tabs>
      <Box sx={{ pt: 2 }}>
        {inner === 0 && (
          <Box {...memberActivityPanelA11yProps(0)}>
            <MemberCreditHistory contactId={contactId} />
          </Box>
        )}
        {inner === 1 && (
          <Box {...memberActivityPanelA11yProps(1)}>
            <MemberActivityTab contactId={contactId} />
          </Box>
        )}
      </Box>
    </>
  );
}
